import { ViewColumnsIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from "react-resizable-panels";
import { useLocation } from "react-router-dom";
import { useAuthStatus } from "../hooks/useAuthStatus";
import ComparisonBars from "../utils/ComparisonBars";
import { fetchMatchWithData } from "../utils/dbutils";
import { updateStats } from "../utils/StatsObject";
import VideoAnalysis from "./VideoAnalysis";

function StatsPage() {
  const { state } = useLocation();

  // Support both old { match } and new { matchGuids, playerGuid } navigation
  const matchGuids = state?.matchGuids ?? (state?.match?.guid ? [state.match.guid] : []);
  const playerGuid = state?.playerGuid ?? state?.match?.player1Guid ?? null;

  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;
  const [matches, setMatches] = useState([]);
  const [statBars, setStatBars] = useState([]);
  const [soPlayer1, setSoPlayer1] = useState(null);
  const [soPlayer2, setSoPlayer2] = useState(null);
  const [cols, setCols] = useState(1);
  const [filteredEventIds, setFilteredEventIds] = useState(undefined);
  const [showPanel, setShowPanel] = useState(true); // show/hide left stats panel

  const CATEGORIES = [
    "Points",
    "Serving",
    "Returning",
    "Break Points",
    "Rally Length",
    "Positioning",
  ];

  const showEvents = (evs) => {
    if (evs !== undefined) {
      const ids = evs
        .map((e) => {
          const eid = e.eventId ?? e.guid ?? e.id;
          if (eid == null) return null;
          return e._matchGuid ? `${e._matchGuid}_${eid}` : String(eid);
        })
        .filter(Boolean);
      setFilteredEventIds(new Set(ids));
      // On mobile, auto-hide the stats panel so the video gets the full width.
      if (typeof window !== "undefined" && window.innerWidth < 768) {
        setShowPanel(false);
      }
    }
  };

  // Generic merger: sums numbers, concatenates arrays, keeps first-match strings
  const mergeStats = (a, b) => {
    if (!a) return { ...b };
    if (!b) return { ...a };
    const result = { ...a };
    for (const key of Object.keys(b)) {
      if (Array.isArray(b[key])) {
        result[key] = [...(a[key] ?? []), ...b[key]];
      } else if (typeof b[key] === "number") {
        result[key] = (a[key] ?? 0) + b[key];
      }
    }
    return result;
  };

  const buildStatBars = (statsA, statsB) => {
    var idx = 1;
    var aw1 = [];
    aw1.push(...statsA.acesEvents);
    aw1.push(...statsA.serveWinnersEvents);
    var aw2 = [];
    aw2.push(...statsB.acesEvents);
    aw2.push(...statsB.serveWinnersEvents);
    var fe1 = [];
    fe1.push(...statsA.forehandForcingErrorsEvents);
    fe1.push(...statsA.backhandForcingErrorsEvents);
    var fe2 = [];
    fe2.push(...statsB.forehandForcingErrorsEvents);
    fe2.push(...statsB.backhandForcingErrorsEvents);
    var ue1 = [];
    ue1.push(...statsA.forehandUnforcedErrorsEvents);
    ue1.push(...statsA.backhandUnforcedErrorsEvents);
    var ue2 = [];
    ue2.push(...statsB.forehandUnforcedErrorsEvents);
    ue2.push(...statsB.backhandUnforcedErrorsEvents);
    var w1 = [];
    w1.push(...statsA.totalWinnersEvents);
    var tp1 = statsA.totalWinnersEvents;
    tp1.push(...fe2);
    tp1.push(...ue2);
    var w2 = [];
    w2.push(...statsB.totalWinnersEvents);
    var tp2 = statsB.totalWinnersEvents;
    tp2.push(...fe1);
    tp2.push(...ue1);
    tp1.push(...statsA.totalPressurePointsWonEvents);
    tp2.push(...statsB.totalPressurePointsWonEvents);

    return [
      {
        id: idx++,
        value1: tp1.length,
        value2: tp2.length,
        label: "Total Points Won",
        category: 0,
        events1: tp1.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1)),
        events2: tp2.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1)),
      },
      {
        id: idx++,
        value1: statsA.totalPressurePointsWon,
        value2: statsB.totalPressurePointsWon,
        label: "Total Pressure Points Won",
        category: 0,
        events1: statsA.totalPressurePointsWonEvents,
        events2: statsB.totalPressurePointsWonEvents,
      },
      {
        id: idx++,
        value1: statsA.totalWinners,
        value2: statsB.totalWinners,
        label: "Winners",
        category: 0,
        events1: w1,
        events2: w2,
      },
      {
        id: idx++,
        value1: statsA.backhandUnforcedErrors + statsA.forehandUnforcedErrors,
        value2: statsB.backhandUnforcedErrors + statsB.forehandUnforcedErrors,
        label: "Unforced Errors",
        category: 0,
        events1: ue1,
        events2: ue2,
      },
      {
        id: idx++,
        value1: statsA.backhandForcingErrors + statsA.forehandForcingErrors,
        value2: statsB.backhandForcingErrors + statsB.forehandForcingErrors,
        label: "Forced Errors",
        category: 0,
        events1: fe1,
        events2: fe2,
      },
      {
        id: idx++,
        value1: statsA.aces + statsA.serveWinners,
        value2: statsB.aces + statsB.serveWinners,
        label: "Serve Aces/Winners",
        category: 1,
        events1: aw1,
        events2: aw2,
      },
      {
        id: idx++,
        value1: statsA.doublefaults,
        value2: statsB.doublefaults,
        label: "Double Faults",
        category: 1,
        events1: statsA.doublefaultsEvents,
        events2: statsB.doublefaultsEvents,
      },
      {
        id: idx++,
        value1: ((statsA.firstServeIns * 100) / statsA.firstServes).toFixed(0),
        value2: ((statsB.firstServeIns * 100) / statsB.firstServes).toFixed(0),
        label: "First Serves In",
        category: 1,
        isPercent: true,
        num1: statsA.firstServeIns,
        den1: statsA.firstServes,
        num2: statsB.firstServeIns,
        den2: statsB.firstServes,
        events1: statsA.firstServeInsEvents,
        events2: statsB.firstServeInsEvents,
      },
      {
        id: idx++,
        value1: ((statsA.firstServicePointsWon * 100) / statsA.firstServeIns).toFixed(0),
        value2: ((statsB.firstServicePointsWon * 100) / statsB.firstServeIns).toFixed(0),
        label: "First Serves Won",
        category: 1,
        isPercent: true,
        num1: statsA.firstServicePointsWon,
        den1: statsA.firstServeIns,
        num2: statsB.firstServicePointsWon,
        den2: statsB.firstServeIns,
        events1: statsA.firstServicePointsWonRallies,
        events2: statsB.firstServicePointsWonRallies,
      },
      {
        id: idx++,
        value1: ((statsA.secondServicePointsWon * 100) / statsA.secondServeIns).toFixed(0),
        value2: ((statsB.secondServicePointsWon * 100) / statsB.secondServeIns).toFixed(0),
        label: "Second Serves Won",
        category: 1,
        isPercent: true,
        num1: statsA.secondServicePointsWon,
        den1: statsA.secondServeIns,
        num2: statsB.secondServicePointsWon,
        den2: statsB.secondServeIns,
        events1: statsA.secondServicePointsWonRallies,
        events2: statsB.secondServicePointsWonRallies,
      },
      {
        id: idx++,
        value1:
          statsA.returnsFirstServe === 0
            ? 0
            : ((statsA.pointsWonOnReturnOffFirstServes * 100) / statsA.returnsFirstServe).toFixed(0),
        value2:
          statsB.returnsFirstServe === 0
            ? 0
            : ((statsB.pointsWonOnReturnOffFirstServes * 100) / statsB.returnsFirstServe).toFixed(0),
        label: "1st Serve Return Points Won",
        category: 2,
        isPercent: true,
        num1: statsA.pointsWonOnReturnOffFirstServes,
        den1: statsA.returnsFirstServe,
        num2: statsB.pointsWonOnReturnOffFirstServes,
        den2: statsB.returnsFirstServe,
        events1: statsA.pointsWonOnReturnOffFirstServesRallies,
        events2: statsB.pointsWonOnReturnOffFirstServesRallies,
      },
      {
        id: idx++,
        value1:
          statsA.returnsSecondServe === 0
            ? 0
            : ((statsA.pointsWonOnReturnOffSecondServes * 100) / statsA.returnsSecondServe).toFixed(0),
        value2:
          statsB.returnsSecondServe === 0
            ? 0
            : ((statsB.pointsWonOnReturnOffSecondServes * 100) / statsB.returnsSecondServe).toFixed(0),
        label: "2nd Serve Return Points Won",
        category: 2,
        isPercent: true,
        num1: statsA.pointsWonOnReturnOffSecondServes,
        den1: statsA.returnsSecondServe,
        num2: statsB.pointsWonOnReturnOffSecondServes,
        den2: statsB.returnsSecondServe,
        events1: statsA.pointsWonOnReturnOffSecondServesRallies,
        events2: statsB.pointsWonOnReturnOffSecondServesRallies,
      },
      {
        id: idx++,
        value1: `${statsA.breakPointsConverted} / ${statsA.breakPoints}`,
        value2: `${statsB.breakPointsConverted} / ${statsB.breakPoints}`,
        label: "Break Points Won",
        category: 3,
        events1: statsA.breakPointsConvertedRallies,
        events2: statsB.breakPointsConvertedRallies,
      },
      {
        id: idx++,
        value1: statsA.totalShortRalliesWon,
        value2: statsB.totalShortRalliesWon,
        label: "Short Rallies (0-4) Won",
        category: 4,
        events1: statsA.totalShortRalliesWonRallies,
        events2: statsB.totalShortRalliesWonRallies,
      },
      {
        id: idx++,
        value1: statsA.totalMediumRalliesWon,
        value2: statsB.totalMediumRalliesWon,
        label: "Medium Rallies (0-4) Won",
        category: 4,
        events1: statsA.totalMediumRalliesWonRallies,
        events2: statsB.totalMediumRalliesWonRallies,
      },
      {
        id: idx++,
        value1: statsA.totalLongRalliesWon,
        value2: statsB.totalLongRalliesWon,
        label: "Long Rallies (0-4) Won",
        category: 4,
        events1: statsA.totalLongRalliesWonRallies,
        events2: statsB.totalLongRalliesWonRallies,
      },
      {
        id: idx++,
        value1: statsA.totalShotsWithin2mOfBaseLine,
        value2: statsB.totalShotsWithin2mOfBaseLine,
        label: "Shots Within 2m of Baseline",
        category: 5,
        events1: statsA.totalShotsWithin2mOfBaseLineEvents,
        events2: statsB.totalShotsWithin2mOfBaseLineEvents,
      },
    ];
  };

  const fetchMatches = async () => {
    if (matchGuids.length === 0 || !uid) return;
    const fetched = (
      await Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
    ).filter(Boolean);
    setMatches(fetched);

    let mergedStatsA = null;
    let mergedStatsB = null;

    for (const m of fetched) {
      // Tag every event with its match guid so filters are globally unique
      for (const s of m.sets ?? []) {
        for (const e of s.events ?? []) {
          e._matchGuid = m.guid;
        }
      }
      const ret = updateStats(m, 0);
      setSoPlayer1(ret.statsA);
      setSoPlayer2(ret.statsB);
      // Normalize so statsA always = focus player/team, statsB = opponent
      const mTeamAGuid = m.isDoubles
        ? (m.teamA?.guid || (m.teamA?.player1?.guid && m.teamA?.player2?.guid ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}` : null))
        : null;
      const focusIsPlayer1 = !playerGuid || m.player1Guid === playerGuid ||
        (m.isDoubles && (mTeamAGuid === playerGuid || m.teamA?.player1?.guid === playerGuid || m.teamA?.player2?.guid === playerGuid));
      const sA = focusIsPlayer1 ? ret.statsA : ret.statsB;
      const sB = focusIsPlayer1 ? ret.statsB : ret.statsA;
      mergedStatsA = mergeStats(mergedStatsA, sA);
      mergedStatsB = mergeStats(mergedStatsB, sB);
    }

    if (!mergedStatsA || !mergedStatsB) return;
    const ss = buildStatBars(mergedStatsA, mergedStatsB);
    setStatBars(ss);
    setCols(Math.ceil(ss.length / 8));
  };

  useEffect(() => {
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchGuids.join(","), uid]);
  return (
    <div className="flex flex-col" style={{ height: '100dvh' }}>
      {/* ── Toolbar ── */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-base-300 bg-base-100">
        <button
          className={`btn btn-sm btn-ghost tooltip tooltip-bottom${showPanel ? " btn-active" : ""}`}
          data-tip={showPanel ? "Hide stats panel" : "Show stats panel"}
          onClick={() => setShowPanel((v) => !v)}
        >
          <ViewColumnsIcon className="w-4 h-4" />
          <span className="text-xs ml-1 hidden sm:inline">Stats</span>
        </button>
        <h1 className="text-lg font-bold">Match Stats</h1>
      </div>
      {/* ── Resizable 2-column body ── */}
      <div className="flex-1 min-h-0">
        <PanelGroup orientation="horizontal" className="h-full" units="pixels">
          {showPanel && (
          <>
          <Panel defaultSize={300} minSize={200} id="panelStats">
            <div className="flex flex-col h-full">
              {/* ── Player names and photos (anchored) ── */}
              <div className="shrink-0 flex items-center justify-around px-2 py-3 border-b border-base-300">
                {(() => {
                  const firstMatch = matches[0];
                  if (!firstMatch) return null;
                  const firstTeamAGuid = firstMatch.isDoubles
                    ? (firstMatch.teamA?.guid || (firstMatch.teamA?.player1?.guid && firstMatch.teamA?.player2?.guid ? `${firstMatch.teamA.player1.guid}_${firstMatch.teamA.player2.guid}` : null))
                    : null;
                  const focusIsPlayer1 = !playerGuid || firstMatch.player1Guid === playerGuid ||
                    (firstMatch.isDoubles && (firstTeamAGuid === playerGuid || firstMatch.teamA?.player1?.guid === playerGuid || firstMatch.teamA?.player2?.guid === playerGuid));
                  const focusPlayer = firstMatch.isDoubles
                    ? (focusIsPlayer1 ? firstMatch.teamA : firstMatch.teamB)
                    : (focusIsPlayer1 ? firstMatch.player1 : firstMatch.player2);
                  const opponentPlayer = matches.length === 1
                    ? (firstMatch.isDoubles
                      ? (focusIsPlayer1 ? firstMatch.teamB : firstMatch.teamA)
                      : (focusIsPlayer1 ? firstMatch.player2 : firstMatch.player1))
                    : null;
                  return [focusPlayer, opponentPlayer].map((p, i) => {
                    if (!p) {
                      return (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div className="w-12 h-12 rounded-full bg-base-300 flex items-center justify-center text-lg font-bold text-base-content/40">
                            ?
                          </div>
                          <span className="text-sm font-semibold text-center text-base-content/40">
                            {matches.length > 1 ? `${matches.length} Opponents` : "—"}
                          </span>
                        </div>
                      );
                    }
                    const initials = p.name
                      ? p.name.split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2)
                      : `${p.firstName?.[0] ?? ""}${p.lastName?.[0] ?? ""}`.toUpperCase();
                    const displayName = p.name ?? `${p.firstName} ${p.lastName?.toUpperCase()}`;
                    return (
                      <div key={i} className="flex flex-col items-center gap-1">
                        {p.photoUrl ? (
                          <img src={p.photoUrl} alt={initials} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary text-primary-content flex items-center justify-center text-lg font-bold">
                            {initials}
                          </div>
                        )}
                        <span className="text-sm font-semibold text-center">
                          {displayName}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* ── Scrollable stats ── */}
              <div className="flex-1 overflow-y-auto">
                <div className="flex-col px-2 py-1">
                  {CATEGORIES.map((cat, i) => (
                    <div key={i} className="flex-col">
                      <div className="text-sm font-bold my-2 text-center">{cat}</div>
                      {statBars
                        .filter((obj) => obj.category === i)
                        .map((sb, j) => (
                          <div key={j} className="mt-4">
                            <ComparisonBars
                              stat={sb}
                              onStatClicked={(evs) => showEvents(evs)}
                            />
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Panel>
          <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
          </>
          )}
          <Panel minSize={200} id="panelVideo">
            {filteredEventIds !== undefined ? (
              <VideoAnalysis
                embeddedMode={true}
                externalMatchGuids={matchGuids}
                externalFilteredIds={filteredEventIds}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-base-content/30 text-sm">
                Click a stat to view events
              </div>
            )}
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}

export default StatsPage;
